<template>
  <div class="transactions-page">
    <PageHeader
      title="Transactions"
      subtitle="Your payment history and downloadable receipts."
    />

    <SectionCard v-if="hasTransactions" flush>
      <v-data-table
        :headers="headers"
        :items="transactions"
        :items-per-page="-1"
        hide-default-footer
        :mobile-breakpoint="600"
        class="transactions-page__table"
      >
        <template v-slot:item.amount="{ item }">
          <span class="transactions-page__amount"
            >{{ item.amount }} {{ item.currency }}</span
          >
        </template>
        <template v-slot:item.payment_status="{ item }">
          <v-chip
            small
            label
            :color="statusChipColor(item.payment_status)"
            :text-color="statusChipTextColor(item.payment_status)"
            class="transactions-page__chip"
          >{{ item.payment_status }}</v-chip>
        </template>
        <template v-slot:item.receipt_url="{ item }">
          <v-btn
            v-if="item.receipt_url"
            text
            small
            color="primary"
            :href="item.receipt_url"
            target="_blank"
            class="transactions-page__receipt"
          >
            <v-icon left small>mdi-file-document-outline</v-icon>Receipt
          </v-btn>
          <span v-else class="transactions-page__no-receipt">—</span>
        </template>
      </v-data-table>
    </SectionCard>

    <SectionCard v-else flush>
      <EmptyState
        icon="mdi-receipt-text-outline"
        title="No transactions yet"
        description="Once you subscribe to a paid plan, your invoices and receipts will show up here."
        action-label="Browse plans"
        @action="onSubscribeClick"
      />
    </SectionCard>

    <div class="loading-overlay" v-if="loading">
      <v-progress-circular
        :size="50"
        :width="2"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
  </div>
</template>

<script>
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import EmptyState from "@/components/shared/EmptyState";

export default {
  name: "Transactions",
  components: { PageHeader, SectionCard, EmptyState },
  data() {
    return {
      headers: [
        { text: "Payment date", value: "payment_date" },
        { text: "Plan", value: "plan_name" },
        { text: "Status", value: "payment_status" },
        { text: "Amount", value: "amount", align: "end" },
        { text: "Receipt", value: "receipt_url", sortable: false, align: "end" },
      ],
      loading: false,
      transactions: [],
    };
  },
  computed: {
    hasTransactions() {
      return this.transactions && this.transactions.length > 0;
    },
  },
  mounted() {
    this.getTransactionHistory();
  },
  methods: {
    statusChipColor(status) {
      const s = (status || "").toLowerCase();
      if (s.includes("success") || s.includes("paid") || s.includes("complete"))
        return "rgba(46, 204, 113, 0.12)";
      if (s.includes("pend")) return "rgba(245, 158, 11, 0.12)";
      if (s.includes("fail") || s.includes("declin") || s.includes("refund"))
        return "rgba(211, 47, 47, 0.12)";
      return "rgba(15, 23, 42, 0.06)";
    },
    statusChipTextColor(status) {
      const s = (status || "").toLowerCase();
      if (s.includes("success") || s.includes("paid") || s.includes("complete"))
        return "#168f4d";
      if (s.includes("pend")) return "#a16207";
      if (s.includes("fail") || s.includes("declin") || s.includes("refund"))
        return "#b91c1c";
      return "#475569";
    },
    getTransactionHistory() {
      this.loading = true;
      this.$store.dispatch("transactions/getTransactionHistory").then(
        (res) => {
          this.loading = false;
          this.transactions = res.data;
        },
        (err) => {
          this.loading = false;
          console.log(err);
        }
      );
    },
    onSubscribeClick() {
      this.$router.push({ name: "Subscriptions" });
    },
  },
};
</script>

<style scoped lang="scss">
.transactions-page {
  position: relative;

  &__table {
    background: transparent !important;

    ::v-deep {
      thead.v-data-table-header th {
        background-color: #f8f9fb !important;
        color: rgba(15, 23, 42, 0.6) !important;
        font-size: 11px !important;
        font-weight: 600 !important;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        padding: 14px 18px;
        border-bottom: 1px solid rgba(15, 23, 42, 0.08) !important;
      }

      thead.v-data-table-header th:first-child {
        border-top-left-radius: 12px;
      }
      thead.v-data-table-header th:last-child {
        border-top-right-radius: 12px;
      }

      tbody td {
        font-size: 13px;
        color: #0f172a;
        padding: 14px 18px !important;
        border-bottom: 1px solid rgba(15, 23, 42, 0.06) !important;
      }

      tbody tr:hover {
        background-color: rgba(15, 23, 42, 0.02) !important;
      }

      tbody tr:last-child td {
        border-bottom: none !important;
      }
    }
  }

  &__amount {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }

  &__chip {
    text-transform: capitalize;
    font-weight: 600;
    font-size: 11px !important;
    height: 22px !important;
  }

  &__receipt {
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 500 !important;
  }

  &__no-receipt {
    color: rgba(15, 23, 42, 0.35);
  }
}
</style>
